import { BooleanInput } from '@angular/cdk/coercion';
import { CommonModule, NgIf } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDividerModule } from '@angular/material/divider';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { Router } from '@angular/router';
import { Select, Store } from '@ngxs/store';
import { Observable, Subscription, fromEvent, of } from 'rxjs';
import { SettingsComponent } from '../settings/settings.component';
import { FuseDrawerComponent } from '@fuse/components/drawer';
import { SignOut } from '@NgXs/authentication/actions/signOut.actions';
import { user } from '@shared/services/user/data';
import { BreakpointSelectors } from '@NgXs/breakpoint/breakpoint.selectors';
import { SelectSobComponent } from '../select-sob/select-sob.component';
import { LoadUserProfile } from '../../../../modules/user-profile/state/user-profile.action';
import { UserProfileSelectors } from '../../../../modules/user-profile/state/user-profile.selector';
import { UserData } from '../../../../modules/user-profile/interfaces/user-profile.interface';
import { AuthService } from '@auth/auth.service';
import { NotificationService } from '@shared/services/notification/notification.service';

import { Permission } from '../../../permissions/enums/permissions.enum';
import { PermissionsService } from '../../../permissions/permissions.service';

@Component({
  selector: 'user',
  templateUrl: './user.component.html',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'user',
  imports: [
    CommonModule,
    MatButtonModule,
    MatMenuModule,
    NgIf,
    MatIconModule,
    MatDividerModule,
    FuseDrawerComponent,
    SettingsComponent,
    SelectSobComponent,
  ],
})
export class UserComponent implements OnInit, OnDestroy {
  static ngAcceptInputType_showAvatar: BooleanInput;
  @Select(UserProfileSelectors.getUserProfileState) userData$: Observable<UserData | null>;
  @Select(BreakpointSelectors.mobileScreen)
  isMobileScreen$: Observable<boolean | null>;

  @Input() showAvatar: boolean = true;

  @ViewChild('settingsDrawer') settingsDrawer: FuseDrawerComponent;

  clickSubscription: Subscription | undefined;

  // Make Permission enum available in template
  Permission = Permission;

  // Track impersonation state
  isImpersonating = false;

  constructor(
    private _router: Router,
    private store: Store,
    private cdr: ChangeDetectorRef,
    private authService: AuthService,
    private notificationService: NotificationService,
    private permissionsService: PermissionsService
  ) {}

  ngOnInit(): void {
    this.store.dispatch(new LoadUserProfile());

    this.clickSubscription = fromEvent(document, 'click').subscribe(() => {
      if (!this.settingsDrawer.opened) this.settingsDrawer.close();
      this.cdr.detectChanges();
    });

    // Subscribe to impersonation state changes
    this.authService.isImpersonating$.subscribe((isImpersonating) => {
      this.isImpersonating = isImpersonating;
      this.cdr.detectChanges();
    });

    // Listen for storage changes to refresh permissions when impersonation ends
    window.addEventListener('storage', this.handleStorageChange.bind(this));
  }

  ngOnDestroy(): void {
    this.clickSubscription.unsubscribe();
    // Remove storage event listener
    window.removeEventListener('storage', this.handleStorageChange.bind(this));
  }

  private handleStorageChange(event: StorageEvent): void {
    // If login data changed, refresh permissions
    if (event.key === 'login') {
      this.cdr.detectChanges();
    }
  }

  signOut(): void {
    this.store.dispatch(new SignOut());
  }

  redirectTo(path: string): void {
    this._router.navigate([path], {
      queryParamsHandling: 'preserve',
    });
    this.cdr.detectChanges();
  }

  openImpersonationDialog(): void {
    // Refresh permissions before checking
    this.refreshPermissions();

    // Check if user has permission to impersonate
    if (!this.hasImpersonationPermission()) {
      this.notificationService.error(
        'Access Denied',
        'You do not have permission to impersonate users.'
      );
      return;
    }

    // Navigate to impersonation route instead of opening dialog
    this._router.navigate(['/impersonation'], {
      queryParamsHandling: 'preserve',
    });
  }

  public hasImpersonationPermission(): boolean {
    // Check if current user is being impersonated - if so, don't show impersonation option
    if (this.authService.hasActiveImpersonation) {
      return false;
    }

    // Get permissions from localStorage
    const permissions = this.permissionsService.Permissions;
    return permissions.includes(Permission.CanImpersonateUsers);
  }

  private refreshPermissions(): void {
    // Force a refresh of the permissions service
    this.permissionsService.Permissions;
    this.cdr.detectChanges();
  }

  protected readonly user = user;
}
